import html2pdf from 'html2pdf.js';

// Helper to create cloned element with PDF-specific styles
const createClonedElement = (elementId) => {
  const element = document.getElementById(elementId);
  if (!element) throw new Error(`Element with id "${elementId}" not found`);

  const clonedElement = element.cloneNode(true);
  clonedElement.style.cssText = `
    width: 100% !important;
    height: auto !important;
    background: white !important;
    color: black !important;
    visibility: visible !important;
    opacity: 1 !important;
    display: block !important;
    margin: 0 auto !important;
    padding: 0 !important;
    box-sizing: border-box !important;
    font-family: Arial, sans-serif !important;
  `;

  // Apply PDF-specific styles
  const styleSheet = document.createElement('style');
  styleSheet.textContent = `
    * {
      box-sizing: border-box !important;
      font-family: Arial, sans-serif !important;
      color: black !important;
    }

    .invoice-container,
    .report-container,
    .aiota-certificate {
      width: 100% !important;
      max-width: 100% !important;
      margin: 0 !important;
      padding: 20px !important;
      background: white !important;
      color: black !important;
      min-height: 100vh !important;
    }

    table {
      width: 100% !important;
      border-collapse: collapse !important;
      margin: 10px 0 !important;
    }

    th, td {
      padding: 8px !important;
      text-align: left !important;
      border: 1px solid #ddd !important;
    }

    th {
      background-color: #f5f5f5 !important;
      font-weight: bold !important;
    }

    .invoice-title,
    .report-title,
    .certificate-title h3 {
      color: #1d4ed8 !important;
      font-weight: bold !important;
      text-align: center !important;
    }

    .invoice-org-name,
    .report-org-title,
    .certificate-header h1 {
      color: #1d4ed8 !important;
      font-weight: bold !important;
      text-align: center !important;
    }
    
    .pdf-temp-container .aiota-logo {
      width: 100px !important;
      height: 100px !important;
      margin: 0 auto 20px auto !important;
      display: block !important;
    }
    
    .pdf-temp-container .certificate-header {
      text-align: center !important;
      margin-bottom: 30px !important;
    }
    
    .pdf-temp-container table {
      page-break-inside: avoid !important;
    }
    
    .pdf-temp-container th,
    .pdf-temp-container td {
      font-size: 12px !important;
    }
  `;

  clonedElement.appendChild(styleSheet);
  return clonedElement;
};

// Common options for html2pdf
const getPdfOptions = (filename, options = {}) => ({
  margin: [0.5, 0.5, 0.5, 0.5],
  filename,
  image: { type: 'jpeg', quality: 0.98 },
  html2canvas: { 
    scale: 2,
    useCORS: true,
    letterRendering: true,
    allowTaint: true,
    backgroundColor: '#ffffff'
  },
  jsPDF: { 
    unit: 'in', 
    format: 'a4', 
    orientation: 'portrait',
    compress: true
  },
  pagebreak: { mode: ['avoid-all', 'css', 'legacy'] },
  ...options
});

// Generate PDF and save
export const generatePDF = async (elementId, filename, options = {}) => {
  const clonedElement = createClonedElement(elementId);
  const pdfOptions = getPdfOptions(filename, options);

  try {
    console.log('Generating PDF for element:', elementId);
    return await html2pdf().set(pdfOptions).from(clonedElement).save();
  } catch (error) {
    console.error('Error generating PDF:', error);
    throw error;
  }
};

// Open PDF in new window
export const openPDFInNewWindow = async (elementId, filename, options = {}) => {
  const clonedElement = createClonedElement(elementId);
  const pdfOptions = getPdfOptions(filename, options);

  try {
    const pdfBlob = await html2pdf().set(pdfOptions).from(clonedElement).outputPdf('blob');
    const pdfUrl = URL.createObjectURL(pdfBlob);
    window.open(pdfUrl, '_blank');
    return pdfUrl;
  } catch (error) {
    console.error('Error opening PDF:', error);
    throw error;
  }
};

// --- Convenience functions for receipts, reports, certificates ---

export const generateReceiptPDF = async (edccData, customFilename = null) => {
  const elementId = 'receipt-pdf-content';
  const filename = `Payment_Receipt.pdf`;
  return await generatePDF(elementId, filename);
};

export const generateReportPDF = async (edccData) => {
  const elementId = 'report-pdf-content';
  const filename = `EDCC_Report_${edccData?.inspection_ref_no || 'N/A'}.pdf`;
  return await generatePDF(elementId, filename);
};

export const generateCertificatePDF = async (edccData, product) => {
  const elementId = `certificate-pdf-content-${product.heading.replace(/\s+/g, '-')}`;
  const filename = `EDCC_Certificate_${product.heading.replace(/\s+/g, '_')}.pdf`;
  return await generatePDF(elementId, filename);
};

export const openReceiptPDF = async (edccData, customFilename = null) => {
  const elementId = 'receipt-pdf-content';
  const filename = customFilename || `EDCC_Receipt_${edccData?.payment_details?.razorpay_payment_id || 'N/A'}.pdf`;
  return await openPDFInNewWindow(elementId, filename);
};

export const openReportPDF = async (edccData) => {
  const elementId = 'report-pdf-content';
  const filename = `EDCC_Report_${edccData?.inspection_ref_no || 'N/A'}.pdf`;
  return await openPDFInNewWindow(elementId, filename);
};

export const openCertificatePDF = async (edccData, product) => {
  const elementId = `certificate-pdf-content-${product.heading.replace(/\s+/g, '-')}`;
  const filename = `EDCC_Certificate_${product.heading.replace(/\s+/g, '_')}.pdf`;
  return await openPDFInNewWindow(elementId, filename);
};
